import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { studentAPI, recordAPI, CATEGORIES, getCategoryInfo } from "../services/api";

const STATUS_BADGE = { draft: "badge-draft", review1: "badge-review1", review2: "badge-review2", final: "badge-final", done: "badge-done" };
const STATUS_LABEL = { draft: "초안 작성 중", review1: "1차 검토 완료", review2: "2차 검토 완료", final: "최종 생성 완료", done: "완료" };

function CharCounter({ text, limit }) {
  const charLimit = limit || 500;
  const count = (text || "").length;
  const cls = count > charLimit ? "over" : count >= charLimit * 0.9 ? "warn" : "";
  return <div className={`char-counter ${cls}`}>{count.toLocaleString()} / {charLimit.toLocaleString()}자</div>;
}

const STAGE_OPTIONS = [
  { value: "review1", label: "1차 검토 결과" },
  { value: "review2", label: "2차 검토 결과" },
  { value: "final",   label: "최종 결과" },
];

const STAGE_CONTENT = {
  review1: (rec) => rec.review1_result,
  review2: (rec) => rec.review2_result,
  final:   (rec) => rec.final_result,
};

// 결과가 하나라도 있는 기록 — 표시 단계 선택 + 결과 + 글자수 + 버튼
function DoneResult({ rec, charLimit, onEdit, onDelete, onStageChange }) {
  const content = STAGE_CONTENT[rec.display_stage]?.(rec) || null;

  return (
    <div>
      {/* 표시 단계 선택 — 결과가 있는 단계만 활성화 */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span className="text-sm text-gray">표시할 결과:</span>
        {STAGE_OPTIONS.map((opt) => {
          const hasResult = !!STAGE_CONTENT[opt.value](rec);
          return (
            <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: ".85rem", cursor: hasResult ? "pointer" : "not-allowed", opacity: hasResult ? 1 : 0.35 }}>
              <input
                type="radio"
                name={`stage-${rec.id}`}
                value={opt.value}
                checked={rec.display_stage === opt.value}
                disabled={!hasResult}
                onChange={() => hasResult && onStageChange(rec.id, opt.value)}
              />
              {opt.label}
            </label>
          );
        })}
      </div>

      {/* 결과 내용 */}
      {content ? (
        <>
          <div className="result-box" style={{ marginBottom: 4 }}>{content}</div>
          <CharCounter text={content} limit={charLimit} />
        </>
      ) : (
        <div className="alert alert-info" style={{ marginBottom: 4 }}>해당 단계 결과가 없습니다.</div>
      )}

      <div className="flex mt-2" style={{ gap: 8 }}>
        <button className="btn btn-outline" style={{ padding: "6px 16px", fontSize: ".85rem" }} onClick={onEdit}>
          {rec.status === "done" ? "편집" : "편집 계속"}
        </button>
        <button className="btn btn-danger" style={{ padding: "6px 16px", fontSize: ".85rem" }} onClick={onDelete}>삭제</button>
      </div>
    </div>
  );
}

// 진행 중 기록의 상태 + 버튼
function InProgressRecord({ rec, onEdit, onDelete }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <span className="text-sm text-gray">
        {rec.updated_at ? new Date(rec.updated_at).toLocaleDateString("ko-KR") : "-"} 수정
      </span>
      <button className="btn btn-primary" style={{ padding: "6px 16px", fontSize: ".85rem" }} onClick={onEdit}>편집 계속</button>
      <button className="btn btn-danger" style={{ padding: "6px 16px", fontSize: ".85rem" }} onClick={onDelete}>삭제</button>
    </div>
  );
}

export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [records, setRecords] = useState([]);
  const [creating, setCreating] = useState(null); // { category, subject, draft }
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const [{ data: stu }, { data: allRecs }] = await Promise.all([
      studentAPI.get(id),
      recordAPI.list(),
    ]);
    setStudent(stu);
    setRecords(allRecs.filter((r) => r.student_id === Number(id)));
  }, [id]);

  const handleStageChange = async (recordId, stage) => {
    setRecords((prev) => prev.map((r) => r.id === recordId ? { ...r, display_stage: stage } : r));
    try {
      await recordAPI.updateDisplayStage(recordId, stage);
    } catch {
      load();
    }
  };

  useEffect(() => { load(); }, [load]);

  const startCreate = (category) => {
    setCreating({ category, subject: "", draft: "" });
    setError("");
  };

  const submitCreate = async () => {
    const cat = getCategoryInfo(creating.category);
    if (cat.needsSubject && !creating.subject.trim()) { setError("과목명을 입력해주세요"); return; }
    if (!creating.draft.trim()) { setError("초안을 입력해주세요"); return; }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await recordAPI.create({
        student_id: Number(id),
        category: creating.category,
        subject: cat.needsSubject ? creating.subject.trim() : null,
        draft: creating.draft,
      });
      navigate(`/record/${data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || "생성 중 오류 발생");
      setSubmitting(false);
    }
  };

  const deleteRecord = async (recordId) => {
    if (!window.confirm("생활기록부를 삭제하시겠습니까? AI 검토 결과도 모두 사라집니다.")) return;
    try {
      await recordAPI.delete(recordId);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "삭제 중 오류 발생");
    }
  };

  if (!student) return <div style={{ padding: 40, textAlign: "center" }}>불러오는 중...</div>;

  const visibleCategories =
    student.tab_type === "subject"
      ? CATEGORIES.filter((c) => c.value === "subject_detail")
      : student.tab_type === "club"
      ? CATEGORIES.filter((c) => c.value === "club")
      : CATEGORIES.filter((c) => c.value !== "subject_detail" && c.value !== "club");

  return (
    <>
      <nav>
        <div className="nav-inner">
          <h1>{student.name} 생활기록부 ({student.tab_type === "homeroom" ? "담임" : student.tab_type === "subject" ? "교과" : "동아리"})</h1>
          <div className="nav-right">
            <button onClick={() => navigate("/")}>← 학생 목록</button>
          </div>
        </div>
      </nav>

      <div className="container" style={{ marginTop: 24 }}>
        {/* 학생 정보 */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
            <div><span className="text-sm text-gray">학생명</span><div className="font-bold">{student.name}</div></div>
            <div><span className="text-sm text-gray">학년/반/번호</span><div className="font-bold">{student.grade}학년 {student.class_num}반 {student.number}번</div></div>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {visibleCategories.map((cat) => {
          const isCreating = creating?.category === cat.value;

          /* ── 교과별 세특 (복수 항목) ── */
          if (cat.needsSubject) {
            const subjectRecords = records.filter((r) => r.category === cat.value);
            return (
              <div key={cat.value} className="card">
                <div className="step-header" style={{ marginBottom: 16 }}>
                  <h3 className="font-bold">{cat.label}</h3>
                  <span className="text-sm text-gray" style={{ marginLeft: 4 }}>과목별 {cat.charLimit}자</span>
                </div>

                {subjectRecords.map((r) => (
                  <div key={r.id} style={{ borderTop: "1px solid #e5e7eb", paddingTop: 14, marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <span className="font-bold">{r.subject || "-"}</span>
                      <span className={`badge ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                    </div>
                    {(r.review1_result || r.review2_result || r.final_result) ? (
                      <DoneResult
                        rec={r}
                        charLimit={cat.charLimit}
                        onEdit={() => navigate(`/record/${r.id}`)}
                        onDelete={() => deleteRecord(r.id)}
                        onStageChange={handleStageChange}
                      />
                    ) : (
                      <InProgressRecord
                        rec={r}
                        onEdit={() => navigate(`/record/${r.id}`)}
                        onDelete={() => deleteRecord(r.id)}
                      />
                    )}
                  </div>
                ))}

                {isCreating ? (
                  <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: 16, background: "#f9fafb", marginTop: subjectRecords.length > 0 ? 8 : 0 }}>
                    <div className="form-group">
                      <label>과목명</label>
                      <input className="form-control" value={creating.subject} onChange={(e) => setCreating({ ...creating, subject: e.target.value })} placeholder="예) 수학, 영어, 물리학I ..." />
                    </div>
                    <div className="form-group" style={{ marginBottom: 4 }}>
                      <label>초안</label>
                      <textarea className="editor-area" style={{ minHeight: 160 }} value={creating.draft} onChange={(e) => setCreating({ ...creating, draft: e.target.value })} placeholder="세부능력 및 특기사항 초안을 작성하세요..." />
                    </div>
                    <CharCounter text={creating.draft} limit={cat.charLimit} />
                    <div className="flex mt-2" style={{ gap: 8 }}>
                      <button className="btn btn-primary" onClick={submitCreate} disabled={submitting}>
                        {submitting ? <><span className="spinner" />저장 중...</> : "저장 후 AI 검토"}
                      </button>
                      <button className="btn btn-outline" onClick={() => setCreating(null)}>취소</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn-outline" style={{ marginTop: subjectRecords.length > 0 ? 4 : 0 }} onClick={() => startCreate(cat.value)}>+ 과목 추가</button>
                )}
              </div>
            );
          }

          /* ── 단일 항목 (자율·동아리·진로·행동특성) ── */
          const rec = records.find((r) => r.category === cat.value);
          return (
            <div key={cat.value} className="card">
              <div className="step-header" style={{ marginBottom: 14 }}>
                <h3 className="font-bold">{cat.label}</h3>
                <span className="text-sm text-gray" style={{ marginLeft: 4 }}>{cat.charLimit}자</span>
                {rec && (
                  <span className={`badge ${STATUS_BADGE[rec.status]}`} style={{ marginLeft: "auto" }}>
                    {STATUS_LABEL[rec.status]}
                  </span>
                )}
              </div>

              {rec ? (
                (rec.review1_result || rec.review2_result || rec.final_result) ? (
                  <DoneResult
                    rec={rec}
                    charLimit={cat.charLimit}
                    onEdit={() => navigate(`/record/${rec.id}`)}
                    onDelete={() => deleteRecord(rec.id)}
                    onStageChange={handleStageChange}
                  />
                ) : (
                  <InProgressRecord
                    rec={rec}
                    onEdit={() => navigate(`/record/${rec.id}`)}
                    onDelete={() => deleteRecord(rec.id)}
                  />
                )
              ) : isCreating ? (
                <div>
                  <textarea
                    className="editor-area"
                    style={{ minHeight: 160 }}
                    value={creating.draft}
                    onChange={(e) => setCreating({ ...creating, draft: e.target.value })}
                    placeholder={`${cat.label} 초안을 작성하세요...`}
                  />
                  <CharCounter text={creating.draft} limit={cat.charLimit} />
                  <div className="flex mt-2" style={{ gap: 8 }}>
                    <button className="btn btn-primary" onClick={submitCreate} disabled={submitting}>
                      {submitting ? <><span className="spinner" />저장 중...</> : "저장 후 AI 검토"}
                    </button>
                    <button className="btn btn-outline" onClick={() => setCreating(null)}>취소</button>
                  </div>
                </div>
              ) : (
                <button className="btn btn-outline" onClick={() => startCreate(cat.value)}>+ 작성 시작</button>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
